import { userRoleLabel } from '@app/features/users/user-role-labels';
import { realmRoles, UserRole } from '@app/features/users/user.model';
import {
  Anomaly,
  AnomalyDisplayState,
  AnomalyStatus,
  BannerAnomaly,
  RESOLVED_DISPLAY_DAYS,
  VisibleAnomaly,
} from './anomaly.model';
import { addDays, formatBound, formatDate, formatDateTime, formatEndDate } from './paris-time';

/**
 * Les bandeaux à afficher. L'API les rend déjà empilés, avec leur état effectif ; une anomalie fermée
 * reste dans la liste pour le menu, mais n'a plus de bandeau.
 */
export function displayedBanners(anomalies: VisibleAnomaly[]): VisibleAnomaly[] {
  return anomalies.filter((anomaly) => anomaly.displayState !== 'CLOSED');
}

/** `depuis le 20/10 à 9 h 52` pour une anomalie en cours, `le 20/10 à 11 h 20` pour une résolue. */
export function bannerMeta(anomaly: BannerAnomaly): string {
  if (anomaly.status === 'RESOLVED') {
    return `le ${formatDateTime(anomaly.resolvedAt ?? anomaly.updatedAt ?? anomaly.displayedFrom)}`;
  }
  return `depuis le ${formatDateTime(anomaly.displayedFrom)}`;
}

function targetsAllRoles(roles: UserRole[]): boolean {
  return realmRoles.every((role) => roles.includes(role));
}

/** `Responsable RH`, `4 rôles`, `tous les rôles`. */
export function rolesSummary(roles: UserRole[]): string {
  if (targetsAllRoles(roles)) return 'tous les rôles';
  if (roles.length === 1) return userRoleLabel(roles[0]);
  return `${roles.length} rôles`;
}

export interface DirectionSections {
  inProgress: Anomaly[];
  scheduled: Anomaly[];
  resolved: Anomaly[];
  archived: Anomaly[];
}

/**
 * Le menu de la direction : toutes les anomalies, dans l'ordre où l'API les rend, rangées selon leur
 * phase à l'heure du serveur plutôt que selon l'horloge du poste.
 */
export function directionSections(anomalies: Anomaly[]): DirectionSections {
  const current = anomalies.filter((a) => !a.archived);
  return {
    inProgress: current.filter((a) => a.phase !== 'SCHEDULED' && a.status === 'IN_PROGRESS'),
    scheduled: current.filter((a) => a.phase === 'SCHEDULED'),
    resolved: current.filter((a) => a.phase !== 'SCHEDULED' && a.status === 'RESOLVED'),
    archived: anomalies.filter((a) => a.archived),
  };
}

/** Les anomalies en cours qui s'affichent maintenant : ce que compte la pastille de la direction. */
export function displayedInProgress(anomalies: Anomaly[]): Anomaly[] {
  return anomalies.filter(
    (a) => !a.archived && a.phase === 'DISPLAYED' && a.status === 'IN_PROGRESS',
  );
}

/** Sous-titre d'une anomalie dans le menu de la direction. */
export function directionSubtitle(anomaly: Anomaly): string {
  const roles = rolesSummary(anomaly.targetRoles);
  const until = anomaly.displayedUntil;
  if (anomaly.phase === 'SCHEDULED') {
    const start = formatDate(anomaly.displayedFrom);
    const period = until ? `Du ${start} au ${formatEndDate(until)}` : `À partir du ${start}`;
    return `${period} · ${roles}`;
  }
  const ended =
    anomaly.phase === 'ENDED' && until
      ? ` · plus affichée depuis le ${formatEndDate(until)}`
      : null;
  if (anomaly.status === 'RESOLVED') {
    const resolved = `Résolue le ${formatDate(anomaly.resolvedAt ?? anomaly.updatedAt ?? anomaly.displayedFrom)}`;
    if (ended) return resolved + ended;
    return until ? `${resolved} · affichée jusqu’au ${formatEndDate(until)}` : resolved;
  }
  return `Depuis le ${formatDate(anomaly.displayedFrom)}${ended ?? ` · ${roles}`}`;
}

const STATE_LABELS: Record<AnomalyDisplayState, string> = {
  EXPANDED: 'dépliée',
  COLLAPSED: 'réduite',
  CLOSED: 'fermée',
};

/** `Depuis le 19/10 · réduite`, `Résolue le 20/10 · fermée`. */
export function userSubtitle(anomaly: VisibleAnomaly): string {
  const when =
    anomaly.status === 'RESOLVED'
      ? `Résolue le ${formatDate(anomaly.resolvedAt ?? anomaly.updatedAt ?? anomaly.displayedFrom)}`
      : `Depuis le ${formatDate(anomaly.displayedFrom)}`;
  return `${when} · ${STATE_LABELS[anomaly.displayState]}`;
}

export interface AudienceInput {
  status: AnomalyStatus;
  targetRoles: UserRole[];
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  resolvedAt?: string | null;
}

export function audience(roles: UserRole[]): string {
  if (targetsAllRoles(roles)) return 'Tous les utilisateurs de CDT';
  if (roles.length === 1) return `Les utilisateurs du rôle ${userRoleLabel(roles[0])}`;
  return `Les utilisateurs des ${roles.length} rôles ciblés`;
}

/**
 * La phrase du dialogue de saisie qui dit à qui et jusqu'à quand l'anomalie sera affichée. `now` est
 * l'heure de Paris, pour compter les deux jours d'une résolution qu'on est en train d'enregistrer.
 */
export function audienceSentence(input: AudienceInput, now: string): string {
  if (!input.targetRoles.length) return 'Choisissez au moins un rôle cible.';
  const who = audience(input.targetRoles);
  const end = input.endDate ? formatBound(input.endDate, input.endTime) : null;
  if (input.status === 'RESOLVED') {
    const until = end ?? formatDate(addDays(input.resolvedAt ?? now, RESOLVED_DISPLAY_DAYS));
    return `${who} verront ce bandeau en vert jusqu’au ${until}, et pourront le fermer.`;
  }
  const from = input.startDate
    ? `à partir du ${formatBound(input.startDate, input.startTime)}`
    : 'dès l’enregistrement';
  const until = end
    ? `jusqu’au ${end}`
    : `tant qu’elle est en cours puis ${RESOLVED_DISPLAY_DAYS} jours après sa résolution`;
  return `${who} verront ce bandeau en orange ${from}, ${until}. Ils pourront le réduire, pas le fermer.`;
}

/** Ce qu'on tentait quand l'API a refusé. */
export type AnomalyAction = 'save' | 'archive' | 'display';

/** Une réponse d'erreur de l'API : son statut, et le corps qui porte parfois un `message`. */
export interface AnomalyApiError {
  status?: number;
  error?: unknown;
}

export function apiMessage(error: AnomalyApiError): string | null {
  const body = error.error;
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = (body as { message: unknown }).message;
    return typeof message === 'string' && message.trim() ? message.trim() : null;
  }
  return null;
}

/**
 * Le refus de l'API, dit en mots. Un 400 reprend le message de l'API quand elle en donne un, sans
 * présumer de sa cause ; un 404 dit une anomalie archivée ou qui ne vise plus l'utilisateur ; un 409
 * un geste que son nouvel état n'autorise plus. `null` pour les autres erreurs, que la fenêtre
 * d'erreur globale prend en charge.
 */
export function anomalyErrorMessage(error: AnomalyApiError, action: AnomalyAction): string | null {
  switch (error.status) {
    case 400: {
      const message = apiMessage(error);
      return message
        ? `L’enregistrement est refusé : ${message}`
        : 'L’enregistrement est refusé. Vérifiez la saisie, puis réessayez.';
    }
    case 404:
      return action === 'display'
        ? 'Cette anomalie ne vous est plus affichée : elle a été archivée, ou ne vise plus votre rôle.'
        : 'Cette anomalie est introuvable.';
    case 409:
      return action === 'display'
        ? 'Cette anomalie est de nouveau en cours : son bandeau se réduit, mais ne se ferme pas.'
        : 'Cette anomalie a été archivée entre-temps : elle ne se modifie plus.';
    default:
      return null;
  }
}
